/**
 * ---------------------------------------------------------------------------------------------
 * Copyright (c) Microsoft Corporation. All rights reserved.
 * Licensed under the MIT License. See License.txt in the project root for license information.
 * ---------------------------------------------------------------------------------------------
 */

import React, { Component } from 'react';
import { FormattedMessage, InjectedIntlProps, injectIntl } from "react-intl";
import { createMessageFactory } from "./../utils/i18n";
import { color } from 'csx';
import { ITeamsThemeContextProps, connectTeamsComponent } from 'msteams-ui-components-react';
import { ThemeStyle } from 'msteams-ui-components-react';
import { mainStyles } from './styles';
import { Pivot, PivotItem, PivotLinkFormat, PivotLinkSize } from 'office-ui-fabric-react/lib/Pivot';
import ReadinessChecklist from './readiness-checklist/readiness-checklist';
import TeamsAppValidator from './teams-app-validator/teams-app-validator';
import Banner from './banner/banner';
import checklist, { IChecklist } from './readiness-checklist/checklist';

const msg = createMessageFactory("main-app-validation");

interface AppProps {
}

interface AppState {
  result: any,
  fileInfo: string,
  checklist: IChecklist[],
  percentComplete: number,
  styles: any,
}

type Props = ITeamsThemeContextProps & AppProps & InjectedIntlProps

class Main extends Component<Props, AppState> {

  constructor(props: Props) {
    super(props);

    this.state = {
      result: null,
      fileInfo: '',
      checklist: checklist,
      percentComplete: 0,
      styles: this.styles(),
    };

    // teams-app-validator
    this.onResultChanged = this.onResultChanged.bind(this);
    this.onFileInfoChanged = this.onFileInfoChanged.bind(this);

    // readiness-checklist
    this.onChecklistChanged = this.onChecklistChanged.bind(this);
    this.onPercentCompleteChanged = this.onPercentCompleteChanged.bind(this);
  }

  public componentDidUpdate(prevProps: any) {
    if (this.props.context.style !== prevProps.context.style) {
      this.setState({ styles: this.styles() });
    }
  }

  public styles = () => {
    const themedColors = this.colors();

    return mainStyles(themedColors)
  }

  private colors = () => {
    const { context } = this.props;

    switch (context.style) {
      case ThemeStyle.HighContrast:
        return {
          focus: context.colors.highContrast.white,
          background: context.colors.highContrast.black,
          shadowColor: 'transparent'
        };
      case ThemeStyle.Dark:
        return {
          mainTitleColor: 'rgb(166, 167, 220)',
          mainTextColor: 'rgb(200, 200, 200)',
          background: 'rgb(24, 24, 24)',
        };
      case ThemeStyle.Light:
      default:
        return {
          mainTitleColor: 'rgb(98, 100, 167)',
          mainTextColor: 'rgb(74, 74, 74)',
          background: color(context.colors.light.white).toRGBA().fade(0.9).toString(),
        };
    }
  }

  private onResultChanged(result: any) {
    this.setState({
      result: result
    });
  }

  private onFileInfoChanged(fileInfo: any) {
    this.setState({
      fileInfo: fileInfo
    });
  }

  private onChecklistChanged(checklist: any) {
    this.setState({
      checklist: checklist
    });
  }

  private onPercentCompleteChanged(percentComplete: number) {
    this.setState({
      percentComplete: percentComplete
    });
  }

  public render(): JSX.Element {
    const styles = this.state.styles;

    return (
      <div>
        <Banner />
        <div className={styles.common.backdrop}>
          <h1 className={styles.common.title}>
            <FormattedMessage
              {...msg({
                id: "app-validation-title",
                defaultMessage: "Validate"
              })}
            />
          </h1>

          <div className={styles.common.subTitle}>
            <FormattedMessage
              {...msg({
                id: "app-validation-sub-title",
                defaultMessage: `Validate your app package manifest or run through this preliminary checklist of top requirements.`
              })}
            />
          </div>

          <div className={styles.tabs.common}>
            <Pivot
              className={styles.tabs.btnContainer}
              linkSize={PivotLinkSize.normal}
              linkFormat={PivotLinkFormat.tabs}
            >
              <PivotItem headerText="Teams app validator">
                <TeamsAppValidator
                  result={this.state.result}
                  fileInfo={this.state.fileInfo}
                  onFileInfoChanged={this.onFileInfoChanged}
                  onResultChanged={this.onResultChanged} />
              </PivotItem>
              <PivotItem headerText="Preliminary checklist">
                <ReadinessChecklist
                  checklist={this.state.checklist}
                  percentComplete={this.state.percentComplete}
                  onPercentCompleteChanged={this.onPercentCompleteChanged}
                  onChecklistChanged={this.onChecklistChanged} />
              </PivotItem>
            </Pivot>
          </div>
        </div>
      </div>
    );
  }

}

export default injectIntl(connectTeamsComponent(Main));
